// SAD-491 (SHAPE-03): Customer Themes surface (B3.2-05).
//
// A suggestion is never visually or texturally confused with an accepted
// theme (B3.2-05.4) — suggested and accepted themes render in two separate
// groups, the suggested group carries a dashed/tinted card plus a header
// strip whose TEXT says "SUGGESTED — not yet accepted by the team", and accepting
// is always an explicit human review-and-submit of a pre-filled form, never
// a one-click adopt. A suggestion can also originate from a human: the New
// theme form files straight to "accepted" by default, but "Save as proposal"
// puts the same hand-authored substance through the same suggested/accept
// arc as an AI draft would.
//
// Contradicting evidence is first-class (B3.2-05.3, GP-07): one evidence
// list per theme, newest-first, never stance-sorted, never truncated.
//
// The "New theme" form works with zero Touchpoints/AI/mappings in existence
// — the ticket's red assertion (GP-01) — so it renders unconditionally, not
// gated behind any prior interaction.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraEmptyState, SpecraHeading, SpecraInput, SpecraOverlayDialog, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorState } from '../Delivery/_shared/components/PageStates';
import { CaptureStakeholdersSection } from './CaptureStakeholdersSection';
import { CustomerContextRelease } from './CustomerContextRelease';
import { CustomerFollowUps } from './CustomerFollowUps';
import { CustomerThemeRelatedWork } from './CustomerThemeRelatedWork';
import { CONFIDENCE_LEVELS, CUSTOMER_THEME_CATEGORIES, CUSTOMER_THEME_EVIDENCE_ASSERTION_KINDS, CUSTOMER_THEME_EVIDENCE_SOURCE_KINDS, CUSTOMER_THEME_MATURITIES, EVIDENCE_STANCES, formatTs, labelOf, themeAuthorshipWording, tsMs } from './constants';
import { openArtifact } from './documentHooks';
import { ReadOnlyScope } from './record/ReadOnlyScope';
import { ThemeDocumentInput } from './ThemeDocumentInput';
import { useAcceptCustomerThemeSuggestion, useAttachCustomerThemeEvidence, useCreateCustomerTheme, useCustomerTheme, useCustomerThemes, useReviseCustomerTheme, useSuggestCustomerTheme } from './themeHooks';
export function CaptureThemesPanel({ pinnedThemeId, pinnedVersionId, captureProjectId, correlationId, hideStakeholders = false, readOnly = false }) {
    const themes = useCustomerThemes(captureProjectId);
    const [addOpen, setAddOpen] = useState(false);
    if (themes.isLoading) {
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.deliveryShared.loadingSkeleton,
            className: "space-y-2",
            children: [
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-10 w-1/3"
                }),
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-32"
                }),
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-32"
                })
            ]
        });
    }
    if (themes.isError) {
        return /*#__PURE__*/ _jsx(ErrorState, {
            title: "Unable to load customer themes",
            error: themes.error
        });
    }
    const all = themes.data ?? [];
    const suggested = all.filter((t)=>t.recordState === 'suggested');
    const accepted = all.filter((t)=>t.recordState === 'accepted');
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            "data-testid": Testids.capture.themesPanelRoot,
            children: [
                !readOnly && /*#__PURE__*/ _jsx(NewThemeForm, {
                    captureProjectId: captureProjectId,
                    correlationId: correlationId,
                    open: addOpen,
                    onOpenChange: setAddOpen
                }),
                !readOnly && all.length > 0 && /*#__PURE__*/ _jsx("div", {
                    className: "flex justify-end",
                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>setAddOpen(true),
                        "data-testid": Testids.pursuitCustomer.themesAddButton,
                        children: "Add theme"
                    })
                }),
                all.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    size: "compact",
                    title: "No customer themes yet",
                    description: "What this customer cares about, in their words.",
                    "data-testid": Testids.pursuitCustomer.themesEmptyState,
                    action: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>setAddOpen(true),
                        "data-testid": Testids.pursuitCustomer.themesAddButton,
                        children: "Add theme"
                    })
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        suggested.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            "data-testid": Testids.capture.themeSuggestedGroup,
                            children: [
                                /*#__PURE__*/ _jsxs("h3", {
                                    className: "text-sm font-medium text-muted-foreground",
                                    children: [
                                        "Proposed — not yet accepted (",
                                        suggested.length,
                                        ")"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "space-y-3",
                                    children: suggested.map((t)=>/*#__PURE__*/ _jsx(ThemeCard, {
                                            theme: t,
                                            pinnedVersionId: t.id === pinnedThemeId ? pinnedVersionId : undefined,
                                            variant: "suggested",
                                            captureProjectId: captureProjectId,
                                            correlationId: correlationId
                                        }, t.id))
                                })
                            ]
                        }),
                        accepted.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            "data-testid": Testids.capture.themeAcceptedGroup,
                            children: [
                                /*#__PURE__*/ _jsxs("h3", {
                                    className: "text-sm font-medium text-muted-foreground",
                                    children: [
                                        "Accepted themes (",
                                        accepted.length,
                                        ")"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "space-y-3",
                                    children: accepted.map((t)=>/*#__PURE__*/ _jsx(ThemeCard, {
                                            theme: t,
                                            pinnedVersionId: t.id === pinnedThemeId ? pinnedVersionId : undefined,
                                            variant: "accepted",
                                            captureProjectId: captureProjectId,
                                            correlationId: correlationId
                                        }, t.id))
                                })
                            ]
                        })
                    ]
                }),
                !hideStakeholders && /*#__PURE__*/ _jsx(CaptureStakeholdersSection, {
                    captureProjectId: captureProjectId,
                    correlationId: correlationId
                })
            ]
        })
    });
}
function NewThemeForm({ captureProjectId, correlationId, open, onOpenChange }) {
    const create = useCreateCustomerTheme(correlationId);
    const suggest = useSuggestCustomerTheme(correlationId);
    const [priority, setPriority] = useState('');
    const [title, setTitle] = useState('');
    const [narrative, setNarrative] = useState('');
    const [whyEvidenceSupports, setWhyEvidenceSupports] = useState('');
    const [whyItMatters, setWhyItMatters] = useState('');
    const [implications, setImplications] = useState('');
    const [category, setCategory] = useState(CUSTOMER_THEME_CATEGORIES[0].value);
    const [maturity, setMaturity] = useState(CUSTOMER_THEME_MATURITIES[0].value);
    const [confidence, setConfidence] = useState(CONFIDENCE_LEVELS[0].value);
    const clearForm = ()=>{
        setTitle('');
        setNarrative('');
        setWhyEvidenceSupports('');
        setWhyItMatters('');
        setImplications('');
        onOpenChange(false);
    };
    // Guard against ever sending an empty title to the backend — the raw
    // "version.title is required" error never has a chance to surface.
    const submit = ()=>{
        const trimmed = title.trim();
        if (!trimmed || create.isPending || suggest.isPending) return;
        create.mutate({
            captureProjectId,
            title: trimmed,
            priority,
            narrative,
            whyEvidenceSupports,
            whyItMatters,
            implications,
            category,
            maturity,
            confidence
        }, {
            onSuccess: clearForm
        });
    };
    // Same substance, filed as a proposal instead of an accepted theme — the
    // suggested/accept arc a human can put a hand-authored theme through
    // rather than committing it straight to "accepted".
    const submitAsProposal = ()=>{
        const trimmed = title.trim();
        if (!trimmed || create.isPending || suggest.isPending) return;
        suggest.mutate({
            captureProjectId,
            originSource: 'manual',
            authorship: 'human',
            title: trimmed,
            priority,
            narrative,
            whyEvidenceSupports,
            whyItMatters,
            implications,
            category,
            maturity,
            confidence
        }, {
            onSuccess: clearForm
        });
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "New theme",
        size: "lg",
        testId: Testids.pursuitCustomer.themesDialog,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: "Manual authoring works here with no prior interaction, AI draft, or mapping — write the theme directly."
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-muted-foreground",
                    children: '"Add accepted theme" files this as the team\'s working belief right away. "Save as proposal" puts it under "Proposed — not yet accepted" instead, until someone reviews and accepts it.'
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "text-xs font-medium",
                            children: "Title"
                        }),
                        /*#__PURE__*/ _jsx(SpecraInput, {
                            value: title,
                            onChange: (e)=>setTitle(e.target.value),
                            placeholder: "What the customer cares about",
                            "data-testid": Testids.capture.themeCreateTitleInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "text-xs font-medium",
                            children: "Narrative"
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: narrative,
                            onChange: (e)=>setNarrative(e.target.value),
                            rows: 2,
                            "data-testid": Testids.capture.themeCreateNarrativeInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "text-xs font-medium",
                            children: "Why the evidence supports this"
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: whyEvidenceSupports,
                            onChange: (e)=>setWhyEvidenceSupports(e.target.value),
                            rows: 2,
                            "data-testid": Testids.capture.themeCreateWhyEvidenceInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "text-xs font-medium",
                            children: "Why it matters"
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: whyItMatters,
                            onChange: (e)=>setWhyItMatters(e.target.value),
                            rows: 2,
                            "data-testid": Testids.capture.themeCreateWhyItMattersInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            className: "text-xs font-medium",
                            children: "Implications"
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: implications,
                            onChange: (e)=>setImplications(e.target.value),
                            rows: 2,
                            "data-testid": Testids.capture.themeCreateImplicationsInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsxs(Select, {
                            value: priority || 'unprioritized',
                            onValueChange: (value)=>setPriority(value === 'unprioritized' ? '' : value),
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    "aria-label": "Theme priority",
                                    "data-testid": Testids.capture.themePrioritySelect,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: "Priority"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SelectContent, {
                                    children: [
                                        'unprioritized',
                                        'low',
                                        'medium',
                                        'high'
                                    ].map((value)=>/*#__PURE__*/ _jsx(SelectItem, {
                                            value: value,
                                            children: value
                                        }, value))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "w-44 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-xs font-medium",
                                    children: "Category"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: category,
                                    onValueChange: setCategory,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.capture.themeCreateCategorySelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: CUSTOMER_THEME_CATEGORIES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "w-40 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-xs font-medium",
                                    children: "Maturity"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: maturity,
                                    onValueChange: setMaturity,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.capture.themeCreateMaturitySelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: CUSTOMER_THEME_MATURITIES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "w-32 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-xs font-medium",
                                    children: "Confidence"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: confidence,
                                    onValueChange: setConfidence,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.capture.themeCreateConfidenceSelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: submit,
                            disabled: !title.trim() || create.isPending || suggest.isPending,
                            "data-testid": Testids.capture.themeCreateSubmitButton,
                            iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                className: "h-4 w-4"
                            }),
                            children: "Add accepted theme"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "secondary",
                            onClick: submitAsProposal,
                            disabled: !title.trim() || create.isPending || suggest.isPending,
                            "data-testid": Testids.capture.themeCreateSuggestButton,
                            children: "Save as proposal"
                        })
                    ]
                })
            ]
        })
    });
}
function ThemeCard({ pinnedVersionId, theme, variant, captureProjectId, correlationId }) {
    const detail = useCustomerTheme(captureProjectId, theme.id);
    const [acceptOpen, setAcceptOpen] = useState(false);
    const pinned = detail.data?.versions.find((v)=>v.id === pinnedVersionId);
    useEffect(()=>{
        if (!pinnedVersionId || !detail.data) return;
        const row = document.getElementById(`theme-${theme.id}`);
        row?.focus({
            preventScroll: true
        });
        row?.scrollIntoView({
            block: 'center'
        });
    }, [
        pinnedVersionId,
        detail.data,
        theme.id
    ]);
    if (detail.isLoading) {
        return /*#__PURE__*/ _jsx(SpecraCard, {
            "data-testid": Testids.capture.themeCard,
            "data-theme-id": theme.id,
            children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                className: "py-4",
                children: /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-20"
                })
            })
        });
    }
    if (detail.isError || !detail.data?.theme) {
        return /*#__PURE__*/ _jsx(SpecraCard, {
            "data-testid": Testids.capture.themeCard,
            "data-theme-id": theme.id,
            children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                className: "py-4",
                children: /*#__PURE__*/ _jsx(ErrorState, {
                    title: "Unable to load this theme",
                    error: detail.error
                })
            })
        });
    }
    const versions = detail.data.versions;
    const evidence = detail.data.evidence;
    const currentVersion = versions.find((v)=>v.id === theme.currentVersionId);
    const historyVersions = versions.filter((v)=>v.id !== theme.currentVersionId).sort((a, b)=>b.version - a.version);
    // Newest-first, never stance-sorted, never truncated (B3.2-05.3, GP-07).
    const sortedEvidence = [
        ...evidence
    ].sort((a, b)=>tsMs(b.createdAt) - tsMs(a.createdAt));
    const contradictCount = evidence.filter((e)=>e.stance === 'contradicts').length;
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.capture.themeCard,
        "data-theme-id": theme.id,
        tabIndex: -1,
        "data-pinned-version-id": pinned?.id,
        id: `theme-${theme.id}`,
        className: variant === 'suggested' ? 'border-dashed border-amber-300 bg-amber-50/40' : '',
        children: [
            variant === 'suggested' && /*#__PURE__*/ _jsxs("div", {
                className: "rounded-t-md border-b border-dashed border-amber-300 bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-900",
                children: [
                    "SUGGESTED — not yet accepted by the team ·",
                    ' ',
                    themeAuthorshipWording(theme.currentVersionAuthorship)
                ]
            }),
            pinnedVersionId && !pinned && /*#__PURE__*/ _jsx(SpecraText, {
                role: "alert",
                children: "The linked theme version is unavailable."
            }),
            pinned && pinned.id !== theme.currentVersionId && /*#__PURE__*/ _jsxs(SpecraCardContent, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraHeading, {
                        level: 3,
                        size: "sm",
                        children: [
                            "Pinned historical theme v",
                            pinned.version,
                            " (read-only)"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: pinned.title
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: pinned.narrative
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        children: [
                            pinned.authoredBy,
                            " · ",
                            formatTs(pinned.createdAt)
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "py-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        "data-testid": Testids.capture.themeTitleBlock,
                        children: currentVersion?.title || 'Untitled theme'
                    }),
                    variant === 'accepted' && /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-muted-foreground",
                        children: [
                            "Accepted by ",
                            theme.acceptedBy || 'unknown',
                            " · ",
                            formatTs(theme.acceptedAt)
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3 text-sm",
                children: [
                    variant === 'accepted' && /*#__PURE__*/ _jsx(CustomerContextRelease, {
                        captureProjectId: captureProjectId,
                        sourceKind: "theme",
                        sourceId: theme.id
                    }),
                    variant === 'accepted' && /*#__PURE__*/ _jsx(CustomerThemeRelatedWork, {
                        captureProjectId: captureProjectId,
                        themeId: theme.id
                    }),
                    variant === 'accepted' && /*#__PURE__*/ _jsx(CustomerFollowUps, {
                        captureProjectId: captureProjectId,
                        sourceKind: "theme",
                        sourceId: theme.id,
                        statement: currentVersion?.whyItMatters || currentVersion?.title || ''
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.capture.themeNarrativeBlock,
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs font-semibold text-muted-foreground",
                                children: "Narrative"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: currentVersion?.narrative || '—'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.capture.themeWhyEvidenceBlock,
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs font-semibold text-muted-foreground",
                                children: "Why the evidence supports this"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: currentVersion?.whyEvidenceSupports || '—'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.capture.themeWhyItMattersBlock,
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs font-semibold text-muted-foreground",
                                children: "Why it matters"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: currentVersion?.whyItMatters || '—'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.capture.themeImplicationsBlock,
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs font-semibold text-muted-foreground",
                                children: "Implications"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: currentVersion?.implications || '—'
                            })
                        ]
                    }),
                    currentVersion && (currentVersion.category || currentVersion.maturity) && /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-muted-foreground",
                        children: [
                            currentVersion.priority && `Priority: ${currentVersion.priority} · `,
                            currentVersion.category && labelOf(CUSTOMER_THEME_CATEGORIES, currentVersion.category),
                            currentVersion.maturity && ` · ${labelOf(CUSTOMER_THEME_MATURITIES, currentVersion.maturity)} maturity`,
                            currentVersion.confidence && ` · ${labelOf(CONFIDENCE_LEVELS, currentVersion.confidence)} confidence`
                        ]
                    }),
                    acceptOpen ? /*#__PURE__*/ _jsx(AcceptSuggestionForm, {
                        revise: variant === 'accepted',
                        captureProjectId: captureProjectId,
                        themeId: theme.id,
                        currentVersion: currentVersion,
                        correlationId: correlationId,
                        onDone: ()=>setAcceptOpen(false),
                        onCancel: ()=>setAcceptOpen(false)
                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        "data-testid": Testids.capture.themeAcceptButton,
                        onClick: ()=>setAcceptOpen(true),
                        children: variant === 'accepted' ? 'Edit theme' : 'Accept'
                    }),
                    historyVersions.length > 0 && /*#__PURE__*/ _jsx(Accordion, {
                        type: "single",
                        collapsible: true,
                        children: /*#__PURE__*/ _jsxs(AccordionItem, {
                            value: "history",
                            children: [
                                /*#__PURE__*/ _jsxs(AccordionTrigger, {
                                    "data-testid": Testids.capture.themeHistoryDisclosure,
                                    children: [
                                        "History (",
                                        historyVersions.length,
                                        " versions)"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(AccordionContent, {
                                    children: /*#__PURE__*/ _jsx("ul", {
                                        className: "space-y-2",
                                        children: historyVersions.map((v)=>/*#__PURE__*/ _jsxs("li", {
                                                className: "rounded border p-2 text-xs",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        className: "font-medium",
                                                        children: [
                                                            "v",
                                                            v.version,
                                                            " · ",
                                                            themeAuthorshipWording(v.authorship),
                                                            " ·",
                                                            ' ',
                                                            formatTs(v.createdAt)
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        children: v.title
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        children: v.narrative
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        children: v.whyEvidenceSupports
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        children: v.whyItMatters
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        children: v.implications
                                                    }),
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        children: [
                                                            "Priority: ",
                                                            v.priority || 'unprioritized',
                                                            " · ",
                                                            v.maturity,
                                                            " · ",
                                                            v.confidence
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        children: [
                                                            v.changeNote,
                                                            " · ",
                                                            v.authoredBy
                                                        ]
                                                    })
                                                ]
                                            }, v.id))
                                    })
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(ThemeEvidenceSection, {
                        captureProjectId: captureProjectId,
                        themeId: theme.id,
                        evidence: sortedEvidence,
                        contradictCount: contradictCount,
                        correlationId: correlationId
                    })
                ]
            })
        ]
    });
}
function AcceptSuggestionForm({ captureProjectId, themeId, currentVersion, correlationId, onDone, onCancel, revise = false }) {
    const accepting = useAcceptCustomerThemeSuggestion(correlationId);
    const revising = useReviseCustomerTheme(correlationId);
    const accept = revise ? revising : accepting;
    const [priority, setPriority] = useState(currentVersion?.priority ?? '');
    const [title, setTitle] = useState(currentVersion?.title ?? '');
    const [narrative, setNarrative] = useState(currentVersion?.narrative ?? '');
    const [whyEvidenceSupports, setWhyEvidenceSupports] = useState(currentVersion?.whyEvidenceSupports ?? '');
    const [whyItMatters, setWhyItMatters] = useState(currentVersion?.whyItMatters ?? '');
    const [implications, setImplications] = useState(currentVersion?.implications ?? '');
    const [category, setCategory] = useState(currentVersion?.category || CUSTOMER_THEME_CATEGORIES[0].value);
    const [maturity, setMaturity] = useState(currentVersion?.maturity || CUSTOMER_THEME_MATURITIES[0].value);
    const [confidence, setConfidence] = useState(currentVersion?.confidence || CONFIDENCE_LEVELS[0].value);
    const submit = ()=>{
        const trimmed = title.trim();
        if (!trimmed || accept.isPending) return;
        accept.mutate({
            captureProjectId,
            themeId,
            title: trimmed,
            priority,
            narrative,
            whyEvidenceSupports,
            whyItMatters,
            implications,
            category,
            maturity,
            confidence
        }, {
            onSuccess: onDone
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 rounded-md border border-blue-200 bg-blue-50 p-2",
        "data-testid": Testids.capture.themeAcceptForm,
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs font-medium text-blue-900",
                children: revise ? 'Revise theme — the previous version stays in history.' : 'Review and edit before accepting — this becomes the human-authored version.'
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                value: title,
                onChange: (e)=>setTitle(e.target.value),
                placeholder: "Title"
            }),
            /*#__PURE__*/ _jsx(SpecraTextarea, {
                value: narrative,
                onChange: (e)=>setNarrative(e.target.value),
                placeholder: "Narrative",
                rows: 2
            }),
            /*#__PURE__*/ _jsx(SpecraTextarea, {
                value: whyEvidenceSupports,
                onChange: (e)=>setWhyEvidenceSupports(e.target.value),
                placeholder: "Why the evidence supports this",
                rows: 2
            }),
            /*#__PURE__*/ _jsx(SpecraTextarea, {
                value: whyItMatters,
                onChange: (e)=>setWhyItMatters(e.target.value),
                placeholder: "Why it matters",
                rows: 2
            }),
            /*#__PURE__*/ _jsx(SpecraTextarea, {
                value: implications,
                onChange: (e)=>setImplications(e.target.value),
                placeholder: "Implications",
                rows: 2
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs(Select, {
                        value: priority || 'unprioritized',
                        onValueChange: (value)=>setPriority(value === 'unprioritized' ? '' : value),
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                "aria-label": "Theme priority",
                                "data-testid": Testids.capture.themePrioritySelect,
                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                    placeholder: "Priority"
                                })
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: [
                                    'unprioritized',
                                    'low',
                                    'medium',
                                    'high'
                                ].map((value)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: value,
                                        children: value
                                    }, value))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Select, {
                        value: category,
                        onValueChange: setCategory,
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                className: "w-40",
                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: CUSTOMER_THEME_CATEGORIES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Select, {
                        value: maturity,
                        onValueChange: setMaturity,
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                className: "w-36",
                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: CUSTOMER_THEME_MATURITIES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Select, {
                        value: confidence,
                        onValueChange: setConfidence,
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                className: "w-32",
                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        onClick: onCancel,
                        children: "Cancel"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: submit,
                        disabled: !title.trim() || accept.isPending,
                        "data-testid": Testids.capture.themeAcceptSubmitButton,
                        children: revise ? 'Save revision' : 'Save accepted theme'
                    })
                ]
            })
        ]
    });
}
function ThemeEvidenceSection({ captureProjectId, themeId, evidence, contradictCount, correlationId }) {
    const attach = useAttachCustomerThemeEvidence(correlationId);
    const [stance, setStance] = useState(EVIDENCE_STANCES[0].value);
    const [assertionKind, setAssertionKind] = useState(CUSTOMER_THEME_EVIDENCE_ASSERTION_KINDS[0].value);
    const [sourceKind, setSourceKind] = useState(CUSTOMER_THEME_EVIDENCE_SOURCE_KINDS[0].value);
    const [sourceRef, setSourceRef] = useState('');
    const [sourceExcerpt, setSourceExcerpt] = useState('');
    const [confidence, setConfidence] = useState(CONFIDENCE_LEVELS[0].value);
    const [rationale, setRationale] = useState('');
    const submit = ()=>{
        if (attach.isPending || sourceKind === 'document' && !sourceRef) return;
        attach.mutate({
            captureProjectId,
            themeId,
            stance,
            assertionKind,
            sourceKind,
            sourceRef,
            sourceExcerpt,
            confidence,
            rationale
        }, {
            onSuccess: ()=>{
                setSourceRef('');
                setSourceExcerpt('');
                setRationale('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 border-t pt-3",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs font-medium",
                "data-testid": Testids.capture.themeEvidenceCountLine,
                children: [
                    evidence.length,
                    " evidence item",
                    evidence.length === 1 ? '' : 's',
                    contradictCount > 0 ? ` — ${contradictCount} contradicts` : ''
                ]
            }),
            evidence.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "No evidence recorded yet."
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1",
                children: evidence.map((e)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": Testids.capture.themeEvidenceRow,
                        "data-stance": e.stance,
                        className: `rounded border px-2 py-1 text-xs ${e.stance === 'contradicts' ? 'border-l-4 border-l-red-600 bg-red-50' : ''}`,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `mr-1 inline-flex items-center rounded-full px-1.5 py-0.5 ${e.stance === 'contradicts' ? 'bg-red-100 font-bold text-red-800' : 'bg-green-50 text-green-700'}`,
                                children: e.stance === 'contradicts' ? 'CONTRADICTS' : 'Supports'
                            }),
                            labelOf(CUSTOMER_THEME_EVIDENCE_SOURCE_KINDS, e.sourceKind),
                            e.sourceKind === 'document' && e.sourceRef ? /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>void openArtifact(e.sourceRef),
                                children: "Open document"
                            }) : e.sourceRef ? ` (${e.sourceRef})` : '',
                            ' ',
                            "· ",
                            labelOf(CONFIDENCE_LEVELS, e.confidence),
                            " confidence",
                            e.sourceExcerpt ? ` — "${e.sourceExcerpt}"` : '',
                            e.rationale ? ` — ${e.rationale}` : ''
                        ]
                    }, e.id))
            }),
            sourceKind === 'document' && /*#__PURE__*/ _jsx(ThemeDocumentInput, {
                captureProjectId: captureProjectId,
                value: sourceRef,
                onChange: setSourceRef
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-40 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Source"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: sourceKind,
                                onValueChange: (value)=>{
                                    setSourceKind(value);
                                    setSourceRef('');
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.themeEvidenceSourceKindSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: CUSTOMER_THEME_EVIDENCE_SOURCE_KINDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-[140px] flex-1 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Reference"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                value: sourceRef,
                                onChange: (e)=>setSourceRef(e.target.value),
                                placeholder: "Link or citation",
                                "data-testid": Testids.capture.themeEvidenceSourceRefInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-44 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Fact vs inference"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: assertionKind,
                                onValueChange: setAssertionKind,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.themeEvidenceAssertionKindSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: CUSTOMER_THEME_EVIDENCE_ASSERTION_KINDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-32 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Stance"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: stance,
                                onValueChange: setStance,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.themeEvidenceStanceSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: EVIDENCE_STANCES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-32 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Confidence"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: confidence,
                                onValueChange: setConfidence,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.themeEvidenceConfidenceSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-[160px] flex-1 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Excerpt"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                value: sourceExcerpt,
                                onChange: (e)=>setSourceExcerpt(e.target.value),
                                rows: 1,
                                placeholder: "Quoted or paraphrased source text",
                                "data-testid": Testids.capture.themeEvidenceSourceExcerptInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-[160px] flex-1 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Rationale"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                value: rationale,
                                onChange: (e)=>setRationale(e.target.value),
                                rows: 1,
                                placeholder: "What this evidence shows",
                                "data-testid": Testids.capture.themeEvidenceRationaleInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: submit,
                        disabled: attach.isPending || sourceKind === 'document' && !sourceRef,
                        "data-testid": Testids.capture.themeEvidenceSubmitButton,
                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                            className: "h-3.5 w-3.5"
                        }),
                        children: "Add evidence"
                    })
                ]
            })
        ]
    });
}
